import React, { useEffect, useState } from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Breadcrumbs from "../../common/Breadcrumbs";
import { fetchProfileData } from "../../api/profileAPI";
import FilePreviewDialog from "../../common/FilePreviewDialog";
import { ProfileDataType } from "../../types/ProfileDataType";
import { renderInfo, renderDocuments } from "./ProfileRenderHelpers";

import useIsMobile from "../../common/mobileVersion/useIsMobile";

const CustomerProfile = () => {
  const [profileData, setProfileData] = useState<ProfileDataType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [previewFileUrl, setPreviewFileUrl] = useState<string | undefined>("");
  const [previewFile, setPreviewFile] = useState<File | undefined>(undefined);
  const [urlFileType, setUrlFileType] = useState("");
  const isMobile = useIsMobile();

  useEffect(() => {
    const getProfileData = async () => {
      try {
        const data = await fetchProfileData(); // Fetch data from mock API
        setProfileData(data);
      } catch (error) {
        console.error("Error fetching profile data:", error);
        setError("Error fetching profile data: Unknown endpoint.");
      } finally {
        setLoading(false);
      }
    };

    getProfileData(); // Call the function to fetch data
  }, []);

  // Loading state
  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (error) {
    return (
      <Typography data-testid="error-message" color="error">
        {error}
      </Typography>
    );
  }

  // Ensure profileData is not null before destructuring
  if (!profileData) {
    return (
      <Typography data-testid="error-message" color="error">
        No profile data found.
      </Typography>
    );
  }
  const { companyDetails, licenseDetails, bankDetails } = profileData;

  const breadcrumbPaths = ["Profile", "My Details"];
  const breadcrumbLinks = [
    "/customer/customer-profile",
    "/customer/customer-profile",
  ];

  const handlePreview = (link: string) => {
    console.log("Preview link:", link);

    // Fetch content type from HEAD request
    fetch(link, { method: "GET" })
      .then((response) => {
        console.log("Response:", response);
        const contentType = response.headers.get("Content-Type") || "unknown";
        console.log("Content-Type:", contentType);
        setUrlFileType(contentType);
      })
      .catch((error) => console.error("Error fetching content type:", error));
    setPreviewFileUrl(link);
    setDownloadUrl(link);
    setOpenDialog(true);
  };
  return (
    <>
      <Breadcrumbs paths={breadcrumbPaths} links={breadcrumbLinks} />
      <Box>
        <Card sx={{ padding: isMobile ? "11px" : "36px 64px", boxShadow: 2 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                paddingBottom: ["16px", "60px"],
                fontSize: isMobile ? "14px" : "inherit",
                fontFamily: "Montserrat",
              }}
            >
              Customer Details
            </Typography>

            {/* Part 1: Company Information */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                paddingBottom: ["16px", "30px"],
                fontSize: isMobile ? "12px" : "inherit",
                fontFamily: "Montserrat",
              }}
            >
              PART 1: Company Information
            </Typography>
            <Grid container rowSpacing={2} columnSpacing={["16px", 4]}>
              {renderInfo("Outlet Name*", companyDetails.outletName)}
              {renderInfo("Company GST Number*", companyDetails.gstNumber)}
              {renderInfo("Company PAN Number*", companyDetails.panNumber)}

              {renderInfo(
                "Company Authorized Signatory (Name)",
                companyDetails.authorizedSignatory
              )}
              {renderInfo("VAT Number (if applicable)", companyDetails.vatNo)}
              {renderInfo("GST State Code", companyDetails.gstStateCode)}
              {renderInfo("Pincode*", companyDetails.pincode)}
              {renderInfo(
                "Email ID*",
                companyDetails.email,
                false,
                false,
                true
              )}
              {renderInfo("Phone Number", companyDetails.phoneNumber)}

              {renderInfo(
                "Registered Address (as per GST)*",
                companyDetails.registeredAddress,
                true,
                true
              )}
            </Grid>

            <Box mt={3}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  paddingBottom: "8px",
                  fontFamily: "Montserrat",
                }}
              >
                Uploaded Documents
              </Typography>
              {renderDocuments(companyDetails.documents, handlePreview)}
            </Box>

            {/* Part 2: License Information */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                paddingTop: "60px",
                paddingBottom: ["16px", "30px"],
                fontSize: isMobile ? "12px" : "inherit",
                fontFamily: "Montserrat",
              }}
            >
              PART 2: License Information
            </Typography>
            <Grid container rowSpacing={2} columnSpacing={["16px", 4]}>
              {renderInfo("License Name*", licenseDetails.licenseName)}
              {renderInfo(
                "Customer Excise Registration Number*",
                licenseDetails.customerExciseRegistrationNumber
              )}
              {renderInfo(
                "License PAN Number*",
                licenseDetails.licensePanNumber
              )}
              {renderInfo(
                "License GST Number*",
                licenseDetails.licenseGstNumber
              )}
              {renderInfo("KSBCL Code*", licenseDetails.ksbclCode)}

              {renderInfo("GST State Code*", licenseDetails.gstStateCode)}
              {renderInfo(
                "Registered Address (as per License)*",
                licenseDetails.licenseAddress,
                true,
                true
              )}
            </Grid>

            <Box mt={3}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  paddingBottom: "8px",
                  fontFamily: "Montserrat",
                }}
              >
                Uploaded Documents
              </Typography>
              {renderDocuments(licenseDetails.documents, handlePreview)}
            </Box>

            {/* Part 3: Bank Information */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                paddingTop: "60px",
                paddingBottom: ["16px", "30px"],
                fontSize: isMobile ? "12px" : "inherit",
                fontFamily: "Montserrat",
              }}
            >
              PART 3: Bank Information
            </Typography>
            <Grid container rowSpacing={2} columnSpacing={["16px", 4]}>
              {renderInfo("Bank Name*", bankDetails.bankName)}
              {renderInfo("Bank Branch*", bankDetails.bankBranch)}
              {renderInfo("Account Type", bankDetails.accountType)}
              {renderInfo("Account Number*", bankDetails.accountNumber)}
              {renderInfo("IFSC Code*", bankDetails.ifscCode)}
              {renderInfo("Beneficiary Name*", bankDetails.beneficiaryName)}
              {renderInfo(
                "Accounts Department POC: Name",
                bankDetails.accountsDepartmentPOCName
              )}
              {renderInfo(
                "Accounts Department POC: Number",
                bankDetails.accountsDepartmentPOCNumber
              )}
              {/* {renderInfo(
                "Credit Remaining",
                bankDetails.accountsDepartmentPOCNumber //change this to have credit field data
              )} */}
            </Grid>

            <Box mt={3}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  paddingBottom: "8px",
                  fontFamily: "Montserrat",
                }}
              >
                Uploaded Documents
              </Typography>
              {renderDocuments(bankDetails.documents, handlePreview)}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <FilePreviewDialog
        open={openDialog}
        downloadUrl={downloadUrl}
        handleClose={() => setOpenDialog(false)}
        previewFileUrl={previewFileUrl}
        previewFile={previewFile}
        setDownloadUrl={setDownloadUrl}
        contentType={urlFileType}
      />
    </>
  );
};

export default CustomerProfile;
